import { useState } from 'react';
import { FileText, Download, ImageOff, MapPin, X } from 'lucide-react';
import type { Message } from '@conversia/shared-types';
import { cn } from '@/lib/cn';
import { esSoloMarcador } from './marcadores';

/**
 * LO QUE VIENE DENTRO DE UN MENSAJE: fotos, audios, videos, documentos.
 *
 * Esteban, 20-sep, mirando su propio chat: *"significa que todo funcione, que
 * se puedan ver imágenes... deben verse las imágenes, reproducir notas de voz"*.
 *
 * Hasta hoy la pantalla del chat pintaba `m.content` y nada más. Un mensaje con
 * una foto se veía como la palabra **[Imagen]** entre corchetes. La foto
 * llegaba, se guardaba y no la miraba nadie.
 *
 * ═══ LA REGLA DE ESTE COMPONENTE: NO FINGIR ═══
 *
 * Hay tres estados distintos y se ven distintos:
 *
 *   1. El archivo está      → se muestra / se reproduce.
 *   2. El mensaje traía un medio pero el archivo NO está → se dice, con el
 *      motivo. WhatsApp caduca los medios viejos y eso no se puede deshacer.
 *   3. No traía medio       → no se pinta nada.
 *
 * El estado 2 es el que importa. Un cuadro gris roto deja al dueño creyendo que
 * la app está fallando ahora; decir "WhatsApp ya no lo guarda" le dice que no
 * hay nada que reintentar. Es la diferencia entre un error y una respuesta.
 *
 * ═══ POR QUÉ NADA SE DESCARGA SOLO ═══
 *
 * Los videos usan `preload="metadata"`: se ve la duración y el primer cuadro
 * sin bajar el archivo entero. Con 44 videos en un chat, `preload="auto"`
 * significa bajarlos todos por abrir la pantalla.
 */

interface Props {
  mensaje: Message;
  /** Para elegir los colores del texto dentro de la burbuja. */
  mio: boolean;
}

/** Cómo se llama cada tipo cuando hay que nombrarlo. */
const NOMBRE: Record<string, string> = {
  IMAGE: 'una imagen',
  AUDIO: 'una nota de voz',
  VIDEO: 'un video',
  DOCUMENT: 'un documento',
  STICKER: 'un sticker',
  LOCATION: 'una ubicación',
};

export function MedioDelMensaje({ mensaje, mio }: Props) {
  const [ampliada, setAmpliada] = useState(false);
  const { mediaType, mediaUrl } = mensaje;

  if (!mediaType) return null;

  // Una ubicación no es un archivo: son dos números que ya viajan en el texto.
  // No hay nada que descargar, así que tampoco hay nada que pueda faltar.
  if (mediaType === 'LOCATION') {
    return (
      <p
        className={cn(
          'flex items-center gap-1.5 text-xs',
          mio ? 'text-white/80' : 'text-slate-500 dark:text-slate-400',
        )}
      >
        <MapPin className="h-3.5 w-3.5 shrink-0" /> Compartió una ubicación
      </p>
    );
  }

  // EL ARCHIVO NO ESTÁ. Se dice, no se pinta un cuadro roto.
  if (!mediaUrl) {
    return (
      <div
        className={cn(
          'flex items-start gap-2 rounded-lg px-2.5 py-2 text-xs',
          mio ? 'bg-white/15 text-white/85' : 'bg-slate-200/70 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300',
        )}
      >
        <ImageOff className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Aquí venía {NOMBRE[mediaType] ?? 'un archivo'} y no lo tenemos.
          <span className="block opacity-80">
            WhatsApp deja de guardar los archivos viejos. Se ve abriendo el chat en el teléfono.
          </span>
        </span>
      </div>
    );
  }

  if (mediaType === 'IMAGE' || mediaType === 'STICKER') {
    const esSticker = mediaType === 'STICKER';
    return (
      <>
        <button
          type="button"
          onClick={() => setAmpliada(true)}
          className="block overflow-hidden rounded-lg"
          aria-label="Ver la imagen en grande"
        >
          <img
            src={mediaUrl}
            alt={esSticker ? 'Sticker' : 'Imagen del mensaje'}
            loading="lazy"
            className={cn(
              'h-auto w-full object-cover',
              // Un sticker a 280px de ancho se ve ridículo: en WhatsApp es chico.
              esSticker ? 'max-h-[120px] w-auto' : 'max-h-[320px]',
            )}
          />
        </button>

        {/* En grande. Sin librería: un div fijo y la tecla Escape del navegador
            no alcanza, así que el fondo entero cierra. */}
        {ampliada && (
          <div
            role="dialog"
            aria-modal="true"
            onClick={() => setAmpliada(false)}
            className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4"
          >
            <button
              type="button"
              aria-label="Cerrar"
              onClick={() => setAmpliada(false)}
              className="absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-full bg-white/15 text-white"
            >
              <X className="h-5 w-5" />
            </button>
            <img
              src={mediaUrl}
              alt="Imagen del mensaje"
              className="max-h-full max-w-full rounded-lg object-contain"
            />
          </div>
        )}
      </>
    );
  }

  if (mediaType === 'AUDIO') {
    return (
      <audio
        controls
        preload="metadata"
        src={mediaUrl}
        // `w-full` con un mínimo: el control nativo de Chrome se rompe por
        // debajo de ~230px y quedan los botones encimados.
        className="w-full min-w-[230px]"
      >
        Tu navegador no puede reproducir esta nota de voz.
      </audio>
    );
  }

  if (mediaType === 'VIDEO') {
    return (
      <video
        controls
        preload="metadata"
        src={mediaUrl}
        className="max-h-[320px] w-full rounded-lg"
      >
        Tu navegador no puede reproducir este video.
      </video>
    );
  }

  // DOCUMENTO. El nombre del archivo, cuando WhatsApp lo mandó, está en el
  // texto del mensaje; si no, se usa el final de la URL.
  const nombre =
    mensaje.content && !esSoloMarcador(mensaje.content)
      ? mensaje.content
      : decodeURIComponent(mediaUrl.split('/').pop() ?? 'archivo');

  return (
    <a
      href={mediaUrl}
      target="_blank"
      rel="noreferrer"
      download
      className={cn(
        'flex min-h-[44px] items-center gap-2 rounded-lg px-2.5 py-2 text-xs',
        mio
          ? 'bg-white/15 text-white hover:bg-white/25'
          : 'bg-slate-200/70 text-slate-700 hover:bg-slate-300/70 dark:bg-slate-700/60 dark:text-slate-200',
      )}
    >
      <FileText className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate">{nombre}</span>
      <Download className="h-3.5 w-3.5 shrink-0 opacity-70" />
    </a>
  );
}
